'use client';

import Button from '@mui/material/Button';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import NextLink from 'next/link';
import type { SxProps, Theme } from '@mui/material/styles';

export default function CTA({
  href,
  text,
  hidden = false,
  newTab = false,
  visible = true,
  variant = 'contained',
  color = 'secondary',
  size = 'big',
  center = true,
  showIcon = true,
  sx,
}: {
  href: string;
  text: string;
  hidden?: boolean;
  newTab?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary';
  visible?: boolean;
  size?: 'big' | 'medium' | 'small';
  center?: boolean;
  showIcon?: boolean;
  sx?: SxProps<Theme>;
}) {
  if (!visible || !href || !text || hidden) return null;

  const buttonProps = {
    href,
  };

  return (
    <Button
      {...buttonProps}
      variant={variant}
      endIcon={showIcon && <EastRoundedIcon />}
      color={color}
      LinkComponent={NextLink}
      size='large'
      className='rounded'
      target={newTab ? '_blank' : '_self'}
      sx={{
        mx: center ? 'auto' : 'inherit',
        width: 'fit-content',
        fontSize: size === 'big' ? { xs: '1rem', md: '1.35rem' } : { xs: '1rem', md: '1rem' },
        fontWeight: 600,
        px: center ? 2 : 1,
        py: 0.25,
        transition: 'transform 0.25s ease-out, box-shadow 0.25s ease-out',
        '& .MuiButton-endIcon': {
          transition: 'transform 0.25s ease-out',
        },
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          '& .MuiButton-endIcon': {
            transform: 'translateX(4px)',
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '& .MuiButton-endIcon': {
            transition: 'none',
          },
          '&:hover': {
            transform: 'none',
            '& .MuiButton-endIcon': {
              transform: 'none',
            },
          },
        },
        ...sx,
      }}
    >
      {text}
    </Button>
  );
}
